<template>
  <div class="md-layout">
    <div class="md-layout-item">
      <!-- Search / Primary Filter Section -->
      <md-card class="qc-card qc-search-card">
        <md-card-content>
          <div class="qc-search-row">
            <!-- Search-by radio pills -->
            <div class="qc-search-left">
              <p class="qc-search-label">Search by</p>

              <div class="search-options">
                <ul>
                  <li v-for="(r, i) in [
                    { label: 'Order Number', value: 'on' },
                    { label: 'PO #', value: 'po' },
                    { label: 'SKU', value: 'sku' },
                    { label: 'Certificates', value: 'cert' },
                  ]" :key="i">
                    <input type="radio" v-model="selectedSearchBy" :value="r.value" />
                    <span>{{ r.label }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Search input -->
            <div class="qc-search-input-wrapper">
              <md-field class="qc-search-field">
                <md-input type="search" id="search-input" clearable
                  placeholder="Search records here... Press enter to search" v-model="searchVal"
                  @keypress="handleSearch($event)"></md-input>
              </md-field>
            </div>

            <!-- Search actions -->
            <div class="qc-search-actions">
              <md-button @click="handleSearch('click')" class="qc-btn qc-btn-primary">
                Search
              </md-button>
              <md-button class="qc-btn qc-btn-secondary" @click="clearFilters">
                Clear
              </md-button>
            </div>
          </div>
        </md-card-content>
      </md-card>

      <!-- Advanced Filters -->
      <md-card class="qc-card qc-filters-card light">
        <md-card-content>
          <div class="filters">
            <div v-if="!activeUser.customerID" class="filter customer">
              <p>Vendors</p>
              <v-select :appendToBody="true" :deselectFromDropdown="true" v-model="filterBy.customers"
                :options="customers" label="name" :reduce="(t) => t.name" :closeOnSelect="true" />
            </div>

            <div v-if="activeUser.isAdmin || activeUser.isSuperAdmin" class="filter customer">
              <p>Sub Vendors</p>
              <v-select :appendToBody="true" :deselectFromDropdown="true" v-model="filterBy.client" :options="clients"
                label="name" :reduce="(t) => t.name" :closeOnSelect="true" />
            </div>

            <div class="filter customer">
              <p>End Task</p>
              <v-select :appendToBody="true" :deselectFromDropdown="true" v-model="filterBy.status"
                :options="['Completed', 'Processing', 'Incomplete-QA In Process']" :closeOnSelect="true" />
            </div>

            <div class="filter">
              <p>Start From</p>
              <md-datepicker class="datepicker" @input="validateDateRange('startDate')" v-model="filterBy.startDate" />
            </div>

            <div class="filter">
              <p>End To</p>
              <md-datepicker class="datepicker" @input="validateDateRange('endDate')" v-model="filterBy.endDate" />
            </div>

            <div class="bottom-row">
              <div class="filter filter-status">
                <p>Show orders with items that</p>
                <div class="status-tags">
                  <span>Failed</span>
                  <md-checkbox v-model="filterBy.showFailedItems" />

                  <span>Passed-W/R</span>
                  <md-checkbox v-model="filterBy.showPassedWithRepair" />

                  <span>Are Certificates</span>
                  <md-checkbox v-model="filterBy.showCertificateOrders" />

                  <span>Shipped</span>
                  <md-checkbox v-model="filterBy.showShippedItems" />
                </div>
              </div>

              <div class="filter filter-btn">
                <md-button class="qc-btn qc-btn-primary" @click="handleFilter">
                  Filter
                </md-button>
                <md-button class="qc-btn qc-btn-secondary" @click="clearFilters">
                  Clear
                </md-button>
              </div>
            </div>
          </div>
        </md-card-content>
      </md-card>

      <!-- Data Table Section -->
      <md-card class="qc-card qc-table-card">
        <md-card-content>
          <div class="qc-table-header">
            <div class="btn-row stats_holder">
              <md-button :class="showExportOptions
                ? 'md-accent'
                : 'md-primary md-round md-small'
                " @click="
                  () => {
                    if (showExportOptions) {
                      showExportOptions = false;
                      showSelectableTable = false;
                      selectedExportData = [];
                    } else {
                      showExportOptions = true;
                    }
                  }
                ">
                {{ showExportOptions ? "Cancel" : "Export" }}
              </md-button>
              <div v-if="showExportOptions" class="qc-export-options">
                <md-button @click="exportCSV('all')" class="md-primary">
                  Export All Table Data
                </md-button>
                <md-button class="md-primary" @click="showSelectableTable = !showSelectableTable">
                  Export Selected Items
                </md-button>
              </div>
              <md-button class="md-round md-small" @click="showTableMenu = !showTableMenu">
                <md-icon translate="no"> menu </md-icon>
              </md-button>

              <div v-if="showTableMenu" class="details-content qc-table-wrapper">
                <div class="table-options">
                  <div class="header">
                    <h3>Customize Table</h3>
                  </div>
                  <ul>
                    <li v-for="option in tableOptions" :key="option">
                      <md-checkbox :value="option" v-model="selectedTableOptions">
                        {{ option }}
                      </md-checkbox>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="qc-table-wrapper">
            <!-- Custom QC Table (replacing md-table) -->
            <table v-if="!showSelectableTable" class="qc-table">
              <thead>
                <tr>
                  <th @click="sortBy('Timestamp')">Date</th>
                  <th @click="sortBy('OrderNum')">Order #</th>
                  <th>Purchase Orders</th>
                  <th v-if="!activeUser.customerID" @click="sortBy('Customer')">
                    Vendors
                  </th>

                  <th v-if="activeUser.isAdmin || activeUser.isSuperAdmin" @click="sortBy('Client')">
                    Sub Vendors
                  </th>
                  <th @click="sortBy('Quantity')">Qty</th>
                  <th @click="sortBy('FailedItems')">Failed</th>
                  <th @click="sortBy('PassedWithRepairsItems')">W/Repairs</th>
                  <th @click="sortBy('SkippedItems')">Skipped</th>
                  <th @click="sortBy('Status')">End Task</th>

                  <th v-if="tableDataToDisplay['SKUS']">SKUS</th>
                  <th v-if="tableDataToDisplay['Certificates']">
                    Certificates
                  </th>
                  <th v-if="tableDataToDisplay['Completed At']">
                    Completed At
                  </th>

                  <th>Controls</th>
                  <th v-if="activeUser.isDeveloper">Dev Admin Controls</th>
                </tr>
              </thead>

              <tbody>
                <template>
                  <!-- MAIN TABLE ROW -->
                  <tr v-for="item in displayedRows" :key="rowKey(item)" class="qc-main-row">
                    <td>{{ item.Timestamp | format_date_time }}</td>
                    <td>{{ item.OrderNum }}</td>
                    <td style="display: flex; justify-content: center">
                      <div class="data-list" v-if="item.PurchaseOrders && item.PurchaseOrders.length">
                        <ul>
                          <li v-for="po in item.PurchaseOrders" :key="po">
                            {{ po }}
                          </li>
                        </ul>
                      </div>
                      <div v-else class="no-data">N/A</div>
                    </td>
                    <td translate="no" v-if="!activeUser.customerID">
                      {{ item.Customer }}
                    </td>
                    <td translate="no" v-if="activeUser.isAdmin || activeUser.isSuperAdmin">
                      {{ item.Client }}
                    </td>
                    <td>{{ item.Quantity }}</td>
                    <td>
                      <strong style="font-weight: 900" v-if="item.FailedItems">{{ item.FailedItems }}</strong>
                      <span v-else>0</span>
                    </td>
                    <td>{{ item.PassedWithRepairsItems || 0 }}</td>
                    <td>{{ item.SkippedItems || 0 }}</td>
                    <td>{{ item.Status }}</td>

                    <td v-if="tableDataToDisplay['SKUS']">
                      <div class="data-list" v-if="item.ItemSKUs.length">
                        <ul>
                          <li v-for="sku in item.ItemSKUs" :key="sku">
                            {{ sku }}
                          </li>
                        </ul>
                      </div>
                      <div v-else class="no-data">N/A</div>
                    </td>

                    <td v-if="tableDataToDisplay['Certificates']">
                      <div class="data-list" v-if="item.ItemCerts && item.ItemCerts.length">
                        <ul>
                          <li v-for="cert in item.ItemCerts" :key="cert">
                            {{ cert }}
                          </li>
                        </ul>
                      </div>
                      <div v-else class="no-data">N/A</div>
                    </td>

                    <td v-if="tableDataToDisplay['Completed At']">
                      <span v-if="item.CompletedAt">
                        {{ item.CompletedAt | format_date_time }}
                      </span>
                      <span v-else>TBD </span>
                    </td>

                    <td class="qc-controls">
                      <md-button v-if="activeUser.isAdmin || activeUser.isSuperAdmin"
                        @click.native="handleViewModal(item)" class="md-just-icon md-flat">
                        <md-icon translate="no" style="color: green">visibility</md-icon>
                      </md-button>

                      <md-button @click.native="showFailedItemReport(item)" class="md-just-icon md-flat">
                        <md-icon translate="no" style="color: gray">receipt_long</md-icon>
                      </md-button>
                    </td>

                    <td v-if="activeUser.isDeveloper" class="qc-controls">
                      <md-button @click.native="deleteOrder(item)" class="md-just-icon md-flat">
                        <md-icon translate="no" style="color: red">delete</md-icon>
                      </md-button>

                      <md-button @click.native="duplicateAndReAssign(item)" class="md-just-icon md-flat">
                        <md-icon translate="no" style="color: gray">save_as</md-icon>
                      </md-button>

                      <!-- <md-button @click.native="editOrder(item)" class="md-just-icon md-flat">
                        <md-icon translate="no" style="color: gray">edit</md-icon>
                      </md-button> -->
                    </td>
                  </tr>

                  <!-- DROPDOWN DETAILS -->
                </template>
              </tbody>
            </table>

            <!-- keep your SelectableTable branch unchanged -->
            <SelectableTable v-else @onSelected="onSelected" :tableData="tableData" :tableInfo="exportItemsInfo">
              <template v-slot:btns>
                <md-button v-if="selectedExportData.length" class="qc-button" @click="exportCSV('selected')">
                  Export
                </md-button>
              </template>
            </SelectableTable>
          </div>
        </md-card-content>

        <md-divider></md-divider>

        <md-card-actions md-alignment="space-between">
          <div class="per-page" :style="{ marginBottom: '20px', marginTop: '20px' }">
            <label for="orders">Per page</label>
            <div>
              <select style="border: none !important" v-model="pagination.perPage" name="orders">
                <option v-for="item in pagination.perPageOptions" :key="item" :label="item" :value="item">
                  {{ item }}
                </option>
              </select>
            </div>
            <p class="card-category">
              Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
            </p>
          </div>

          <div v-if="activeUser.isAdmin || activeUser.isSuperAdmin" class="footer">
            <md-button class="qc-button" @click="showAuthModal = true">
              Authenticate
            </md-button>
          </div>

          <Pagination class="pagination-no-border pagination-success" v-model="pagination.currentPage"
            :per-page="pagination.perPage" :total="total" @update-table="fetchData"
            :allowClick="Boolean(tableData.length + searchedData.length)"></Pagination>
        </md-card-actions>
      </md-card>

      <!-- All your modals (unchanged) -->

      <!-- Table Modal for when a user clicks on an order  -->
      <TableModal @qc-mult="handleMultipleItemsQA" :showFilterForItemTable="true" :showItemFilter="true"
        :orderNumber="selectedOrder.id" :orderLineStatus.sync="selectedOrderLineStatus" :lineItems.sync="allLineItems"
        :showDialog.sync="showDialog" :lineItemsInfo.sync="lineItemsInfo" :itemsInfo="itemsInfo"
        @selectedLineItemAction="selectedLineItemAction" @selectedItemAction="selectedItemAction"
        @close-modal="closeTableModal()" @open-qc="openQC"
        @handleSearchForIndividualItem="handleSearchForIndividualItem">
      </TableModal>

      <!-- Modal for Individual Line Items   -->
      <Modal :showBackBtn="true" @back="goBack('showModalContentForLineItem', 'showDialog')"
        @close="showModalContentForLineItem = false" :showDialog="showModalContentForLineItem">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content>
              <div class="row">
                <md-field style="min-width: fit-content">
                  <label>Vendors</label>
                  <md-input disabled :value="selectedOrder.Customer"></md-input>
                </md-field>
                <md-field style="max-width: 120px">
                  <label>Order #</label>
                  <md-input disabled :value="selectedOrder.OrderNum"></md-input>
                </md-field>
                <md-field>
                  <label>Date/Time</label>
                  <md-input disabled :value="selectedOrder.Timestamp | format_date_time"></md-input>
                </md-field>
                <md-field style="max-width: 70px">
                  <label>QTY</label>
                  <md-input disabled :value="selectedLineItem.Quantity"></md-input>
                </md-field>
              </div>
              <div>
                <p>Description</p>
                <md-field>
                  <md-textarea @input="showChangesBtn = true" v-model="selectedLineItem.Description"></md-textarea>
                </md-field>
                <md-button class="qc-button" v-if="showChangesBtn" @click="saveDescription(selectedLineItem)">
                  Save Changes
                </md-button>
              </div>
            </md-card-content>
            <md-divider></md-divider>
            <md-card-content>
              <Table :tableData.sync="allItemsForSelectedLine" :tableInfo="itemsInfo"
                @selected-content="selectedItemAction"></Table>
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <!-- Modal for Individual Item  -->
      <Modal :showBackBtn="true" @back="goBack('showModalContentForItem', 'showDialog')"
        :showDialog="showModalContentForItem" @close="showModalContentForItem = false">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content>
              <div class="row">
                <md-field>
                  <label>Order #</label>
                  <md-input disabled :value="selectedOrder.OrderNum"></md-input>
                </md-field>
                <md-field>
                  <label>SKU</label>
                  <md-input disabled v-if="selectedItem.ClientSKU" :value="selectedItem.ClientSKU"></md-input>
                  <md-input disabled v-if="selectedItem.SKU" :value="selectedItem.SKU"></md-input>
                </md-field>
                <md-field>
                  <label>Item Type</label>
                  <md-input disabled :value="selectedItem.Type"></md-input>
                </md-field>
              </div>
              <div style="margin-bottom: 16px !important">
                <img :style="{
                  cursor: '-moz-zoom-in',
                  cursor: '-webkit-zoom-in',
                  cursor: 'zoom-in',
                }" @click="handleImageModal(selectedItem.ImageURL)" :src="selectedItem.ImageURL" alt="" />
              </div>
            </md-card-content>
          </md-card>
        </template>
        <template v-slot:dialog-actions>
          <md-button @click="
            (showItemImagesModal = true), (selectedItemType = 'individual')
            " class="btn all-text-btn">
            Add New Photo
          </md-button>
          <md-button class="qc-button" @click="openQC(selectedItem)">
            Q/A Item
          </md-button>
        </template>
      </Modal>

      <!-- Modal for Image View   -->
      <Modal @close="
        () => {
          (showImageModal = false), (imageView = null);
        }
      " :showSaveBtn="false" :showDialog="showImageModal" :showBackBtn="true"
        @back="goBack('showImageModal', 'showOrderReportModal')">
        <template v-slot:modalContent>
          <div class="image-container">
            <img v-if="imageView" :style="{ width: imageSize }" @click="handleImageModal(imageView)" :src="imageView"
              alt="" />

            <div v-else class="no-image">Image not found</div>
          </div>
        </template>
      </Modal>

      <!-- Modal for Q/A   -->

      <QARejectionModal :isVisible="showQCModal" :item="selectedItem" @close="clearQCResponses"
        :existingRejections="qcResponses.rejections" @submit="handleRejectionSelection" />

      <!-- Modal for Q/A Multiple Items   -->
      <Modal @close="showQCModalForMult = false" :showSaveBtn="false" :showDialog="showQCModalForMult"
        :showBackBtn="false" :modalContainerStyle="{ width: '90vw !important' }">
        <template v-slot:modalContent>
          <div class="toggle_tabs">
            <div class="tab" v-for="(item, t_index) in allFailedItems" :key="t_index"
              :class="{ active: popupTab == t_index }">
              <p>{{ item.CertNum || item.ItemNum }}</p>
            </div>
          </div>
          <div v-for="(item, index) in allFailedItems" :key="index" class="inputs">
            <transition name="fade">
              <div v-if="popupTab == index">
                <QAMultipleItems :item="item" @submitForAll="handleQAForAllSelectedItems" @submit="
                  handleQAPerItem(
                    $event,
                    item,
                    popupTab === allFailedItems.length - 1,
                    false
                  )
                  " :lastItem="popupTab === allFailedItems.length - 1" @submitMultQAData="finishQCProtocol" />
              </div>
            </transition>
          </div>
        </template>
      </Modal>

      <!-- Modal for Auth   -->
      <Modal @save="handleAuthChanges" @close="closeAuthModal" :showSaveBtn="Boolean(selectedAuthItems.length)"
        :showBackBtn="false" :showDialog="showAuthModal">
        <template v-slot:modalContent>
          <md-card style="width: 800px">
            <md-card-content>
              <div class="filters">
                <md-field class="column">
                  <label>Search By Order/Line #</label>
                  <md-input @keydown.enter.prevent="handleAuthSearchOnce" v-model="searchAuthItems"></md-input>
                </md-field>

                <md-button class="qc-button" @click="handleAuthSearch">
                  Find
                </md-button>
              </div>
            </md-card-content>
            <md-divider></md-divider>
            <md-card-content>
              <table class="table-wide">
                <tr class="qc-title">
                  <th></th>
                  <th>Order #</th>
                  <th>Line #</th>
                  <th>Cert/Item #</th>
                  <th>SKU</th>
                  <th>Type</th>

                  <th>
                    <input value="Passed" @input="handleStatuses($event, 'Passed')"
                      :checked="statusForAllItems == 'Passed'" type="checkbox" />Pass
                  </th>

                  <th>
                    <input value="Failed" @input="handleStatuses($event, 'Failed')"
                      :checked="statusForAllItems == 'Failed'" type="checkbox" />Failed
                  </th>

                  <th>
                    <input value="Skipped" @input="handleStatuses($event, 'Skipped')"
                      :checked="statusForAllItems == 'Skipped'" type="checkbox" />Skip
                  </th>

                  <th>
                    <input value="Passed-W/Repair" @input="handleStatuses($event, 'Passed-W/Repair')"
                      :checked="statusForAllItems == 'Passed-W/Repair'" type="checkbox" />Passed-W/Repair
                  </th>
                </tr>
                <tr v-for="(item, index) in allItemsForAuthentication" :key="item.id">
                  <td>{{ index + 1 }}</td>
                  <td class="table-center">{{ item.OrderNum }}</td>
                  <td class="table-center">{{ item.LineNum }}</td>
                  <td class="table-center">
                    {{ item.CertNum || item.ItemNum }}
                  </td>
                  <td class="table-center">{{ item.SKU || item.ClientSKU }}</td>
                  <td class="table-center">{{ item.Type }}</td>
                  <td class="table-center">
                    <input value="Passed" @change="handleSelectedAuthItem(item, 'add')" v-model="item.Status"
                      type="radio" />
                  </td>
                  <td class="table-center">
                    <input value="Failed" @change="handleSelectedAuthItem(item, 'add')" v-model="item.Status"
                      type="radio" />
                  </td>
                  <td class="table-center">
                    <input value="Skipped" @change="handleSelectedAuthItem(item, 'add')" v-model="item.Status"
                      type="radio" />
                  </td>
                  <td class="table-center">
                    <input value="Passed-W/Repair" @change="handleSelectedAuthItem(item, 'add')" v-model="item.Status"
                      type="radio" />
                  </td>
                </tr>
              </table>
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <!-- Modal for Selecting QA Tech   -->

      <Modal @save="saveAuthChanges" :showSaveBtn="true" :showBackBtn="false" :showDialog="showQAAssociateModal">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content>
              <md-card>
                <md-card-content>
                  <p>Select QA Associate</p>
                  <v-select :appendToBody="true" :deselectFromDropdown="true" v-model="selectedQaAssociate"
                    :options="allQaAssociates" :clearable="false" :closeOnSelect="true" />
                </md-card-content>
              </md-card>
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <!-- Modal for Displaying Items Reports   -->
      <Modal @close="closeReportModal" :showDialog="showOrderReportModal" :showBackBtn="false"
        :modalContainerStyle="{ overflow: 'hidden' }">
        <template v-slot:modalContent>
          <md-card class="qc-card qc-search-card">
            <md-card-content>
              <div class="qc-search-row">
                <div class="qc-search-left">
                  <p class="qc-search-label">Search by</p>

                  <div class="search-options">
                    <ul>
                      <li v-for="(r, i) in [
                        { label: 'Line Number', value: 'LineNum' },
                        { label: 'P.O #', value: 'PO' },
                        { label: 'SKU', value: 'SKU' },
                        { label: 'Certificates', value: 'CertNum' },
                        { label: 'Item', value: 'ItemNum' },
                      ]" :key="i">
                        <input type="radio" v-model="selectedSearchByForAllOrderItems" :value="r.value" />
                        <span>{{ r.label }}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Search input -->
                <div class="qc-search-input-wrapper">
                  <md-field class="qc-search-field">
                    <md-input placeholder="Search Item Here..." @input="handleReportSearch"></md-input>
                  </md-field>
                </div>
              </div>
            </md-card-content>
          </md-card>
          <md-divider></md-divider>
          <md-card class="qc-card qc-table-card">
            <md-card-content>
              <div class="qc-table-header">
                <md-field>
                  <label class="qc-header-label">Customer</label>
                  <md-input disabled :value="selectedOrder.Customer"></md-input>
                </md-field>
                <md-field>
                  <label class="qc-header-label">Order #</label>
                  <md-input disabled :value="selectedOrder.OrderNum"></md-input>
                </md-field>
                <md-field>
                  <label class="qc-header-label">Date/Time</label>
                  <md-input disabled :value="selectedOrder.Timestamp | format_date_time"></md-input>
                </md-field>
                <md-field>
                  <label class="qc-header-label">QTY</label>
                  <md-input disabled :value="selectedOrder.Quantity"></md-input>
                </md-field>
              </div>
            </md-card-content>

            <md-divider></md-divider>

            <!-- Real buttons, so tabs work with the keyboard -->
            <div class="toggle_tabs" role="tablist">
              <button v-for="tab in ['Line Item Status', 'Item Status']" :key="tab" type="button" role="tab" class="tab"
                :class="{ active: tableTab === tab }" :aria-selected="tableTab === tab" @click="tableTab = tab">
                {{ tab }}
              </button>
            </div>

            <!-- One transition around both tabs, so the fade actually runs on switch -->
            <transition name="fade" mode="out-in">
              <!-- ===== Line Item Status ===== -->
              <md-card-content v-if="tableTab == 'Line Item Status'">
                <transition name="fade">
                  <div class="qc-table-wrapper">
                    <table class="qc-table">
                      <thead>
                        <tr>
                          <th>Line #</th>
                          <th>P.O #</th>
                          <th>SKU</th>
                          <th>Type</th>
                          <th>QTY</th>
                          <th>Failed</th>
                          <th>W/ Repair</th>
                          <th>Skipped</th>
                          <th>Notes</th>
                          <th>QA Images</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr style="text-align: center" v-for="(item, k) in unfilteredAllOrderLineItemsInfo" :key="k"
                          class="qc-main-row" :class="{ 'failed-items': item.failedQty || item.repairedQty }">
                          <td>{{ item.LineNum }}</td>
                          <td>{{ item.PO || 'N/A' }}</td>
                          <td>
                            <div class="sku-container">
                              {{ item.ClientSKU || 'N/A' }}
                            </div>
                          </td>
                          <td>{{ item.ItemType }}</td>
                          <td>{{ item.Quantity }}</td>
                          <td>{{ item.failedQty || 0 }}</td>
                          <td>{{ item.repairedQty || 0 }}</td>
                          <td>{{ item.skippedQty || 0 }}</td>
                          <td style="display: flex;
                                flex-direction: column;
                                height: 200px;
                                justify-content: center;">
                            <div v-if="item.Notes.length" class="notes">
                              <ol style="padding: 0">
                                <li class="result-list-item" v-for="(rejection, i) in item.Notes" :key="rejection">
                                  <span>{{ i + 1 }}.{{ rejection }}</span>
                                </li>
                              </ol>

                            </div>
                            <div v-else class=" no-content">
                              N/A
                            </div>

                          </td>
                          <td>
                            <div style="display: flex; flex-direction: column">
                              <button v-if="item.Images.length" @click="
                                (showItemImagesModal = true),
                                (selectedFailedItem = item),
                                (selectedItemType = 'failed')
                                " class="btn all-text-btn">
                                View
                              </button>
                              <div v-else class="no-content">
                                N/A
                              </div>

                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </md-card-content>

              <!-- ===== Item Status (grouped by line) ===== -->
              <md-card-content v-else key="items">
                <div class="qc-table-wrapper">
                  <table class="qc-table">
                    <thead>
                      <tr>
                        <th>Line #</th>
                        <th>P.O #</th>
                        <th>SKU</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th>QA Images</th>
                        <th>Shipped</th>
                        <th v-if="showQAAssociate">Authenticated By</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr v-if="!groupedOrderItems.length">
                        <td :colspan="itemColspan" class="no-content">No items</td>
                      </tr>

                      <template v-for="group in groupedOrderItems">
                        <tr :key="`group-${group.lineNum}`" class="qc-line-header">
                          <td :colspan="itemColspan">

                            Line {{ group.lineNum }}
                            <span class="qc-line-count">
                              {{ group.entries.length }} item{{ group.entries.length === 1 ? "" : "s" }}
                              <template v-if="group.passedCount">
                                · <span class="qc-line-failed" style="color: green;">{{ group.passedCount }}
                                  passed</span>
                              </template>
                              <template v-if="group.failedCount">
                                · <span class="qc-line-failed">{{ group.failedCount }} failed</span>
                              </template>
                              <template v-if="group.repairCount">
                                · <span class="qc-line-failed" style="color: #395907;">{{ group.repairCount }}
                                  passed-w/repair</span>
                              </template>
                              <template v-if="group.skippedCount">
                                · <span class="qc-line-failed" style="color: orange;">{{ group.skippedCount }}
                                  skipped</span>
                              </template>
                            </span>
                          </td>
                        </tr>

                        <tr v-for="{ item, index } in group.entries" :key="`item-${item.StockNum || index}`"
                          class="qc-main-row" :class="{ 'failed-items': item.Status === 'Failed' }">

                          <td>{{ item.LineNum }}</td>
                          <td>{{ item.PO || "N/A" }}</td>
                          <td>
                            <div class="sku-container">{{ item.SKU || "N/A" }}</div>
                          </td>
                          <td>{{ item.Type }}</td>
                          <td>{{ item.Status }}</td>
                          <td>
                            <div class="qc-cell-stack">
                              <div v-if="noteLines(item).length"
                                :class="{ expanded_notes: noteLines(item).length > 1 }">
                                <div v-for="(note, i) in noteLines(item)" :key="`note-${i}`" class="qc-note-line">
                                  {{ note }}
                                </div>
                              </div>
                              <!-- index = position in the full sorted list, same as your old `k` -->
                              <button type="button" class="btn all-text-btn" @click="handleNoteModal(item, index)">
                                {{ noteLines(item).length ? "Edit" : "Add Notes" }}
                              </button>
                            </div>
                          </td>
                          <td>
                            <div class="qc-cell-stack">
                              <button v-if="(item.Images || []).length" type="button" class="btn all-text-btn"
                                @click="openImages(item)">
                                View
                              </button>
                              <button type="button" class="btn all-text-btn" @click="openImages(item)">
                                Upload
                              </button>
                            </div>
                          </td>
                          <td v-if="item.HasBeenShipped">
                            <div class="qc-shipped-date">{{ item.ShippedOn | format_date_time }}</div>
                            <sub v-if="item.TrackingNum">
                              <a class="qc-tracking-link" :href="trackingUrl(item.TrackingNum)" target="_blank"
                                rel="noopener noreferrer">
                                {{ item.TrackingNum }}
                              </a>
                            </sub>
                          </td>
                          <td v-else>Not Shipped</td>
                          <td v-if="showQAAssociate">{{ item.QA_Associate || "TBD" }}</td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </md-card-content>
            </transition>
          </md-card>
        </template>
      </Modal>

      <!-- Modal for Adding Notes on Report Modal -->
      <Modal :showDialog="showNoteModal" :showBackBtn="false" :showSaveBtn="true" @close="closeNoteModal"
        @save="handleSaveNote">
        <template v-slot:modalContent>
          <div class="notes-container" style="display: flex; flex-direction: column">
            <h3>Notes:</h3>
            <md-field>
              <md-textarea v-if="selectedItem.Responses" v-model="selectedItem.Responses.notes"></md-textarea>
            </md-field>
          </div>
        </template>
      </Modal>

      <!-- Modal for Displaying Failed Item Images  -->
      <Modal @close="closeImageModal" :showDialog="showItemImagesModal" :showBackBtn="true"
        @back="goBack('showItemImagesModal', 'showOrderReportModal')">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content>
              <MultImages @removeImage="removeImage" @uploadedImages="uploadedImages" :media="selectedFailedItem.Images"
                :item="selectedFailedItem" />
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <!-- Modal For Displaying Who Authenticated -->
      <Modal @close="closeAuthViewModal" :showDialog="showAuthViewModal" :showBackBtn="false">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content class="row">
              <md-field>
                <label>Vendors</label>
                <md-input disabled :value="selectedOrder.Customer"></md-input>
              </md-field>
              <md-field>
                <label>Order #</label>
                <md-input disabled :value="selectedOrder.OrderNum"></md-input>
              </md-field>
              <md-field>
                <label>Date/Time</label>
                <md-input disabled :value="selectedOrder.Timestamp | format_date_time"></md-input>
              </md-field>
              <md-field>
                <label>QTY</label>
                <md-input disabled :value="selectedOrder.Quantity"></md-input>
              </md-field>
            </md-card-content>
            <md-divider></md-divider>
            <md-card-content>
              <table class="table-wide">
                <tr class="qc-title">
                  <th>Line #</th>
                  <th>Cert/Item #</th>
                  <th>SKU</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th v-if="activeUser.isAdmin || activeUser.isSuperAdmin">
                    Authenticated By
                  </th>
                </tr>
                <tr style="text-align: center" v-for="(item, k) in allLineItems" :key="k">
                  <td>{{ item.LineNum }}</td>
                  <td>{{ item.CertNum || item.ItemNum }}</td>
                  <td>{{ item.SKU || item.ClientSKU }}</td>
                  <td>{{ item.Type }}</td>
                  <td>{{ item.Status }}</td>
                  <td v-if="item.QA_Associate">
                    {{ item.QA_Associate }}
                  </td>
                  <td v-else-if="selectedOrder.QA_Associate">
                    {{ selectedOrder.QA_Associate }}
                  </td>
                  <td v-else>Not Authenticated</td>
                </tr>
              </table>
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <DuplicateOrderModal @modalClose="showDuplicateModal = false" :modelValue="showDuplicateModal"
        :order="selectedOrderForDuplication" :clients="clients" />
      <input id="barcode-input" ref="barcodeInput" autofocus type="search" style="opacity: 0"
        placeholder="Search or Scan records here" />
    </div>

    <md-snackbar md-position="center" :md-duration="5000" :md-active.sync="showSnackbar" md-persistent>
      <span>{{ snackbarMsg }}</span>
    </md-snackbar>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import moment from "moment";
import Pagination from "@/components/Pagination.vue";
import TableModal from "@/components/TableModal.vue";
import Modal from "@/components/Modal.vue";
import Table from "@/components/Table.vue";
import SelectableTable from "@/components/SelectableTable.vue";
import MultImages from "@/components/Inputs/UploadMultiple.vue";
import { db, functions } from "@/config/firebaseInit.js";
import DuplicateOrderModal from '@/components/DuplicateOrderModal.vue'

import firebase from "firebase";
import QARejectionModal from "../components/QARejectionModal.vue";
import QAMultipleItems from "../components/QAMultipleItems.vue";
const ExcelJS = require("exceljs");
const { DateTime } = require("luxon");

export default {
  name: "Home",
  components: {
    Pagination,
    TableModal,
    Modal,
    Table,
    MultImages,
    SelectableTable,
    DuplicateOrderModal,
    QARejectionModal,
    QAMultipleItems
  },
  computed: {
    showQAAssociate() {
      return !!(this.activeUser && this.activeUser.isAdmin && this.activeUser.isSuperAdmin);
    },
    itemColspan() {
      return this.showQAAssociate ? 9 : 8;
    },
    /**
     * Items sorted by LineNum, then grouped. Each entry keeps `index`,
     * its position in the full sorted list (what the old template passed as `k`).
     */
    groupedOrderItems() {
      const reboot = this.selectedOrder
      const sorted = Object.values(this.unfilteredAllOrderItemsInfo || {}).sort(
        (a, b) => Number(a.LineNum) - Number(b.LineNum),
      );
      const groups = new Map();
      sorted.forEach((item, index) => {
        const lineNum = item.LineNum != null ? item.LineNum : "Unassigned";
        if (!groups.has(lineNum)) groups.set(lineNum, []);
        groups.get(lineNum).push({ item, index });
      });
      return [...groups.entries()].map(([lineNum, entries]) => ({
        lineNum,
        entries,
        failedCount: entries.filter((e) => e.item.Status === "Failed").length,
        skippedCount: entries.filter((e) => e.item.Status === "Skipped").length,
        passedCount: entries.filter((e) => e.item.Status === "Passed").length,
        repairCount: entries.filter((e) => e.item.Status === "Passed-W/Repair").length,
      }));
    },
    /***
     * Returns a page from the searched data or the whole data.
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData.slice(this.from, this.to);
      }
      return result;
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.tableSize;
    },

    // how many columns the details row should span
    detailsColspan() {
      let cols = 8;

      if (this.activeUser?.isAdmin || this.activeUser?.isSuperAdmin) cols++;
      if (!this.activeUser?.customerID) cols++;

      return cols + 1;
    },

    displayedRows() {
      const rows = [...this.queriedData];

      return rows.sort((a, b) => {
        const field = this.currentSort;
        const dir = this.currentSortOrder === "asc" ? 1 : -1;

        const av = a[field] ?? "";
        const bv = b[field] ?? "";

        if (typeof av === "number" && typeof bv === "number") {
          return (av - bv) * dir;
        }
        return String(av).localeCompare(String(bv)) * dir;
      });
    },

    unfilteredAllOrderItemsInfo() {
      let results = {};

      if (this.searchValueForAllOrderItems.length) {
        this.searchValueForAllOrderItems.forEach((item) => {
          results[item.id] = item;
        });
      } else {
        results = this.allOrderItemsInfo;
      }
      return results;
    },

    unfilteredAllOrderLineItemsInfo() {
      let results = {};

      if (this.searchValueForAllOrderItems.length) {
        this.searchValueForAllOrderItems.forEach((item) => {
          results[item.ItemNum] = item;
        });
      } else {
        results = this.allOrderLineInfo;
      }
      return results;
    },

    filteredRejections() {
      // If search is empty, returns the whole list.
      // Otherwise, filters by matching text.
      const query = this.searchQuery.toLowerCase();
      return this.rejectionOptionByType[this.jewelryType].filter((reason) =>
        reason.toLowerCase().includes(query)
      );
    },
    // This helper chunks the array into pairs for your 2-column table
    chunkedRejections() {
      let results = [];
      for (let i = 0; i < this.filteredRejections.length; i += 2) {
        results.push(this.filteredRejections.slice(i, i + 2));
      }
      return results;
    },
  },

  data() {
    return {
      isSearchingAuth: false,
      showDuplicateModal: false,
      selectedRejection: "",
      selectedOrderForDuplication: {},
      imageView: null,
      jewelryType: "Ring",
      selectedItemToExpand: null,
      showTableMenu: false,
      expandedRows: {},
      currentSort: "Timestamp",
      currentSortOrder: "desc",
      activeUser: {},
      selectedItemType: "",
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [50, 250, 500, 1000],
        total: 0,
      },
      searchQuery: "",
      searchVal: "",
      selectedSearchBy: "on",
      selectedSearchByForAllOrderItems: "LineNum",
      allItemsForAuthentication: [],
      tableOptions: ["Purchase Orders", "SKUS", "Certificates", "Completed At"],
      isBeingHandledThroughAuthWindow: false,
      tableDataToDisplay: {
        "Purchase Orders": false,
        SKUS: false,
        Certificates: false,
        "Completed At": false,
      },
      selectedTableOptions: [],
      filterBy: {
        showFailedItems: false,
        showPassedItems: false,
        showShippedItems: false,
        showPassedWithRepair: false,
        client: "",
        status: "",
        customers: "",
        startDate: "",
        endDate: "",
      },
      tableSize: 0,
      propsToSearch: ["title", "fullname", "description"],
      showModalContentForLineItem: false,
      showQAAssociateModal: false,
      showModalContentForItem: false,
      selectedQaAssociate: "",
      allQaAssociates: [],
      showImageModal: false,
      showSelectableTable: false,
      showSnackbar: false,
      statusForAllItems: null,
      showChangesBtn: false,
      allFailedItems: [],
      allItemsForSelectedLine: [],
      selectedLineItem: {},
      selectedItem: {},
      selectedFailedItem: {},
      showModalContent: false,
      showExportOptions: false,
      selectedExportData: [],
      searchValueForAllOrderItems: [],
      searchAuthItems: null,
      popupTab: null,
      tableTab: "Line Item Status",
      lineItemsInfo: [
        // {
        //   label: "Customer",
        //   key: "Customer",
        // },
        // {
        //   label: "Order #",
        //   key: "OrderNum",
        // },
        {
          label: "Line #",
          key: "LineNum",
        },
        {
          label: "Qty",
          key: "Quantity",
        },
      ],
      itemsInfo: [
        {
          label: "Line #",
          key: "LineNum",
        },
        {
          label: "Item #",
          key: "ItemNum",
        },
        {
          label: "Vendor",
          key: "Customer",
        },
        {
          label: "SKU",
          key: "SKU",
        },

        {
          label: "Cert #",
          key: "CertNum",
        },

        {
          label: "Type",
          key: "Type",
        },

        {
          label: "Status",
          key: "Status",
        },
      ],
      itemsInfoForAuth: [
        {
          label: "Cert #",
          key: "CertNum",
        },
        {
          label: "Type",
          key: "Type",
        },
        {
          label: "Description",
          key: "Description",
        },
      ],
      exportItemsInfo: [
        {
          label: "Order #",
          key: "OrderNum",
        },
        {
          label: "Vendor",
          key: "Customer",
        },
        {
          label: "Sub Vendor",
          key: "Client",
        },

        {
          label: "Quantity",
          key: "Quantity",
        },
        {
          label: "FailedItems",
          key: "FailedItems",
        },
        {
          label: "Status",
          key: "Status",
        },
      ],
      unfilteredItems: [],
      tableData: [],
      selectedOrderLineStatus: [],
      allLineItems: [],
      searchedData: [],
      selectedItems: [],
      customers: [],
      clients: [],
      showDialog: false,
      showQCModal: false,
      showQCModalForMult: false,
      showAuthModal: false,
      showOrderReportModal: false,
      showItemImagesModal: false,
      showNoteModal: false,
      allOrderItemsInfo: {},
      allOrderLineInfo: {},
      selectedOrder: {},
      ordersRef: {},
      leftQCPanelInit: false,
      showAuthViewModal: false,
      qcResponses: {

        rejections: [],
        notes: "",
      },
      rejectionOptionByType: {
        Bracelet: [
          "Loose Clasp",
          "Broken Clasp",
          "Clasp Not Fully Closing",
          "Hard to Open Clasp",
          "Weak Spring in Clasp",
          "Bent Tongue",
          "Faulty Locking Mechanism",
          "Open Link",
          "Broken Link",
          "Bent Link",
          "Uneven Links",
          "Uneven Link Spacing",
          "Loose Link Pin",
          "Poor Solder Joint",
          "Loose Stone",
          "Missing Stone",
          "Crooked Stone",
          "Broken Prong",
          "Bent Prong",
          "Uneven Setting",
          "Seat Too Deep",
          "Seat Too Shallow",
          "Scratches",
          "Polishing Marks",
          "Uneven Plating",
          "Peeling Plating",
          "Rhodium Stains",
          "Color Mismatch",
          "Metal Porosity",
          "Bent Bracelet",
          "Incorrect Length",
          "Asymmetry",
          "Sharp Edge",
          "Uneven Thickness",
          "Misaligned Logo",
          "Unclear Stamp",
        ],
        Studs: [
          "Loose Post",
          "Bent Post",
          "Misaligned Post",
          "Post Too Short",
          "Post Too Long",
          "Post Too Thin",
          "Post Too Thick",
          "Post Not 90° to Head",
          "Rough Post Tip",
          "Scratched Post",
          "Loose Back",
          "Tight Back",
          "Bent Back",
          "Uneven Back",
          "Faulty Clutch",
          "Post/Back Mismatch",
          "Loose Stone",
          "Stone Tilt",
          "Off-Center Stone",
          "Stone Table Not Level",
          "Uneven Setting",
          "Bent Prong",
          "Broken Prong",
          "Prong Too Short",
          "Prong Too Long",
          "Prong Not Contacting Girdle",
          "Metal Scratches",
          "Porosity",
          "Polish Marks",
          "Uneven Plating",
          "Discoloration",
          "Pair Mismatch",
          "Size Mismatch",
          "Weight Mismatch",
        ],
        Earrings: [
          "Loose Stone",
          "Toolmark",
          "Missing Stone",
          "Crooked Stone",
          "Off-Center Stone",
          "Scratched Stone",
          "Chipped Stone",
          "Bent Prong",
          "Broken Prong",
          "Short Prong",
          "Long Prong",
          "Uneven Prongs",
          "Prong Over-Covering",
          "Bent Post",
          "Short Post",
          "Long Post",
          "Post Not 90°",
          "Sharp Post Tip",
          "Loose Back",
          "Tight Back",
          "Mismatched Back",
          "Misaligned Back Hole",
          "Metal Scratches",
          "Metal Porosity",
          "Worn Plating",
          "Poor Soldering",
          "Size Mismatch",
          "Color Mismatch",
          "Pair Asymmetry",
        ],
        "Eternity Band": [
          "Missing Stone",
          "Toolmark",
          "Loose Stone",
          "Cracked Stone",
          "Broken Stone",
          "Crooked Stone",
          "Uneven Stone Height",
          "Off-Center Stone",
          "Crooked Setting",
          "Shallow Setting",
          "Deep Setting",
          "Crooked Channel",
          "Over-Tight Setting",
          "Gaps Between Stones",
          "Uneven Spacing",
          "Broken Prong",
          "Bent Prong",
          "Prong Too Short",
          "Prong Too Long",
          "Weak Prong",
          "Sharp Prong Tip",
          "Prong Not Securing",
          "Out of Round",
          "Warped Band",
          "Uneven Thickness",
          "Metal Crack",
          "Porosity",
          "Deep Scratches",
          "Sharp Edge",
          "Back Wear",
          "Uneven Thickness",
          "Metal Crack (Circumference)",
        ],
        Pendant: [
          "Loose Clasp",
          "Toolmark",
          "Broken Clasp",
          "Clasp Not Fully Closing",
          "Hard to Open Clasp",
          "Weak Spring",
          "Bent Tongue",
          "Unsafe Lock",
          "No Safety Catch",
          "Open Link",
          "Broken Link",
          "Bent Link",
          "Uneven Links",
          "Poor Solder Joint",
          "Loose Pin",
          "Tangling Chain",
          "Uneven Tension",
          "Crooked Pendant",
          "Off-Center Pendant",
          "Open Bail",
          "Broken Bail",
          "Weak Pendant Connection",
          "Pendant Twisting",
          "Loose Stone",
          "Missing Stone",
          "Crooked Stone",
          "Broken Prong",
          "Bent Prong",
          "Uneven Setting",
          "Seat Too Deep",
          "Seat Too Shallow",
          "Scratches",
          "Polishing Marks",
          "Uneven Plating",
          "Peeling Plating",
          "Rhodium Stains",
          "Color Mismatch",
          "Metal Porosity",
          "Incorrect Length",
          "Bent Necklace",
          "Asymmetry",
          "Sharp Edge",
          "Unclear Stamp",
          "Misaligned Logo",
        ],
        Necklace: [
          "Loose Clasp",
          "Toolmark",
          "Broken Clasp",
          "Clasp Not Fully Closing",
          "Hard to Open Clasp",
          "Weak Spring",
          "Bent Tongue",
          "Unsafe Lock",
          "No Safety Catch",
          "Open Link",
          "Broken Link",
          "Bent Link",
          "Uneven Links",
          "Poor Solder Joint",
          "Loose Pin",
          "Tangling Chain",
          "Uneven Tension",
          "Crooked Pendant",
          "Off-Center Pendant",
          "Open Bail",
          "Broken Bail",
          "Weak Pendant Connection",
          "Pendant Twisting",
          "Loose Stone",
          "Missing Stone",
          "Crooked Stone",
          "Broken Prong",
          "Bent Prong",
          "Uneven Setting",
          "Seat Too Deep",
          "Seat Too Shallow",
          "Scratches",
          "Polishing Marks",
          "Uneven Plating",
          "Peeling Plating",
          "Rhodium Stains",
          "Color Mismatch",
          "Metal Porosity",
          "Incorrect Length",
          "Bent Necklace",
          "Asymmetry",
          "Sharp Edge",
          "Unclear Stamp",
          "Misaligned Logo",
        ],
        Ring: [
          "Asymmetrical Halo",
          "Bent Post",
          "Bent Prong",
          "Broken or Bent Prong",
          "Broken or Fractured Metal",
          "Broken or Wavy Post",
          "Broken Prong",
          "Casting Cavity",
          "Chain Broken/Damaged",
          "Chipped Girdle",
          "Chipped Stone(s)",
          "Clasp Not Function Properly",
          "Cracked Stone",
          "Crooked Channel",
          "Crooked or Titled Head",
          "Crooked or Titled Stone",
          "Damaged Item or Component",
          "Deep Scratches",
          "Detached Component(s)",
          "Distorted After Sizing",
          "Enhancements/Imitations",
          "Excess Metal",
          "Eye-visible Inclusion",
          "Eye-visible Pits/Porosity/Pin Holes",
          "Eye-visible Scratches",
          "Eye-visible Toolmarks/gouges",
          "Frayed or Missing Knots",
          "Gap at Prong",
          "Improper millimeter size",
          "Inclusion/Structural Integrity",
          "Incorrect Size",
          "Inner Cut Mark",
          "Item Differs From Sample",
          "Jumpring Not Well Soldered",
          "Loose Center Stone",
          "Loose or Tight Earnut(s)",
          "Loose Side Stone",
          "Loose Stone",
          "Loose Stones",
          "Low Carat Weight",
          "Low Clarity",
          "Low Color",
          "Low Cut Grade",
          "Low Luster",
          "Material Failure",
          "Metal Composition",
          "Metal Crack",
          "Micro/Structural Porosity",
          "Misaligned/Askew",
          "Mismatched Stones or Pearls",
          "Missing Earring Backs",
          "Missing Halo Stone",
          "Missing/Incorrect Stamp",
          "Missing Stone",
          "Off-Center Stone",
          "Overall Asymmetry",
          "Out of Round",
          "Pavilion Touching Metal",
          "Pearl came unglued from peg",
          "Pearls not strung to standards",
          "Polish Marks",
          "Poor Polish and Finish",
          "Porosity",
          "Prong Not Contacting Girdle",
          "Prong Too Long",
          "Prong Too Short",
          "Rough Area",
          "Scratched Table",
          "Seat Too Deep",
          "Seat Too Shallow",
          "Sharp Edge",
          "Sharp Prong Tip",
          "Stone Tilt",
          "Table Not Level",
          "Toolmark",
          "Uneven Plating", // Note: Plating and Polish were distinct in lists
          "Uneven Polish",
          "Uneven Prong Height",
          "Uneven Seat",
          "Uneven Stone Height",
          "Uneven Spacing",
          "Uneven Thickness",
          "Visible Solder",
          "Warped Band",
          "Weak Prong",
        ],

      },
      selectedAuthItems: [],
      snackbarMsg: "",
      inputValue: "",
      barcodeValue: "",
      imageSize: "100%",
      allLineItemsUnfiltered: [],
      certificateAPI: functions.httpsCallable(
        "certificateAPI-updateCertificate"
      ),
      // routeChange: true,
    };
  },

  async created() {
    this.activeUser = this.$store.state.user;

    if (this.$store.state.user.isSuperAdmin) this.getQAAssociate();
    else this.selectedQaAssociate = this.$store.state.user.fullname;
    this.ordersRef = db.collection("orders");
    this.$bind("null", db.doc("analytics/orders")).then((doc) => {
      this.tableSize = doc.total;
    });
    this.fetchClients()
    this.fetchCustomers()

    this.fetchData("start");
    this.originPath = window.location.origin;
  },


  watch: {
    selectedRejection(newValue) {
      if (!newValue) return;

      try {
        // We keep the folder path static so Webpack can index the directory
        // The @ alias usually points to the 'src' directory
        this.imageView = require(`@/assets/img/${this.jewelryType}/${newValue}.png`);
      } catch (e) {
        console.error("Image not found", e);
        this.showImageModal = false;
        this.showItemImagesModal = true;
        this.imageView = ""; // Fallback to a placeholder
      }
    },
    popupTab(tab) {
      if (this.allFailedItems[tab] && this.allFailedItems[tab].Type) {
        this.jewelryType = this.allFailedItems[tab].Type.trim();
      }
    },
    selectedItem(item) {
      if (item.Type) this.jewelryType = item.Type.trim();
    },

    selectedTableOptions: {
      deep: true,
      handler(selected) {
        this.tableDataToDisplay = {
          "Purchase Orders": false,
          SKUS: false,
          Certificates: false,
          "Completed At": false,
        };
        selected.forEach((option) => {
          this.tableDataToDisplay[option] = true;
        });
      },
    },
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleBarcodeScanned);
  },
  mounted() {
    window.addEventListener("keydown", this.handleBarcodeScanned);
    this.$nextTick(() => {
      const inputElement = this.$refs.barcodeInput.$el;
      if (inputElement && typeof inputElement.focus === "function") {
        inputElement.focus();
      }
    });
  },
  beforeDestroy() {
    this.unbindListener();
  },
  methods: {
    noteLines(item) {
      const notes = item.Responses && item.Responses.notes;
      return notes ? notes.split("\n").filter((n) => n.trim()) : [];
    },
    openImages(item, type = "failed") {
      this.selectedFailedItem = item;
      this.selectedItemType = type;
      this.showItemImagesModal = true;
    },
    trackingUrl(trackingNum) {
      return `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(trackingNum)}`;
    },
    async handleAuthSearchOnce() {
      if (this.isSearchingAuth) return;

      this.isSearchingAuth = true;

      try {
        await this.handleAuthSearch();
      } finally {
        this.isSearchingAuth = false;
      }
    },
    async fetchClients() {
      let clients = await db.collection("clients").get()
      this.clients = clients.docs.map(d => Object.assign({ id: d.id, }, d.data()))
    },
    async fetchCustomers() {
      let customers = await db.collection("customers").get()
      this.customers = customers.docs.map(d => Object.assign({ id: d.id, }, d.data()))
    },
    handleQAPerItem(data, item, lastItem, save4all) {
      const qcResponses = { rejections: [], notes: null }
      qcResponses.rejections = data.selectedRejections
      qcResponses.notes = data.description
      this.saveQC(item, qcResponses, save4all)
      if (lastItem) this.finishQCProtocol()
    },
    handleQAForAllSelectedItems(data) {
      const currentItem = this.selectedAuthItems[this.popupTab]
      const allItemsWithSameRejections = this.selectedAuthItems.slice(this.popupTab).filter(i => i.Status == 'Failed')

      allItemsWithSameRejections.forEach((item, index) => {
        const lastItem = index == allItemsWithSameRejections.length - 1
        if (currentItem.Images.length) {
          db.doc(`orders/${item.OrderNum}/items/${item.ItemNum}`)
            .update({
              Images: currentItem.Images,
            })
        }
        this.handleQAPerItem(data, item, lastItem, true)
      })
      this.finishQCProtocol()
    },
    handleRejectionSelection(data) {
      this.qcResponses.rejections = data.selectedRejections
      this.qcResponses.notes = data.description
      this.handleQCChanges()
    },
    deleteOrder(order) {
      const orderRef = db.collection("orders").doc(order.id);
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.value) {
          await orderRef.delete()
          return Swal.fire("Deleted!", "The data has been deleted.", "success");
        }
      });
    },
    duplicateAndReAssign(order) {
      this.showDuplicateModal = true
      this.selectedOrderForDuplication = order
    },
    fetchRejectionImage(item, rejection) {
      try {
        return require(`@/assets/img/${item.Type}/${rejection}.png`) || "";
      } catch (error) {
        console.log({ error });
        return null;
      }
    },

    openRejectionImage(item, rejection) {
      this.selectedFailedItem = item;
      this.selectedRejection = rejection;
      this.showOrderReportModal = false;
      this.showImageModal = true;
      this.imageSize = "50%";
    },

    async fetchOrder(query) {
      if (!query && this.barcodeValue) query = this.barcodeValue;
      if (query) {
        let orderNum = "";
        let LineNum = "";
        if (query.includes("-")) {
          orderNum = query.split("-")[0];
          LineNum = query.split("-")[1];
        }
        this.barcodeValue = "";
        const orderRef = await db.doc(`orders/${orderNum}`).get();
        if (!orderRef.exists) {
          this.showSearchInputModal = false;

          return Swal.fire({
            title: "No Records Found",
            buttonsStyling: true,
            html: `<b>No Records</b> has been found. <br/> Please refer to Diamond Track
            and confirm this order exists`,
          });
        } else {
          const order = orderRef.data();
          if (LineNum) {
            this.selectedOrder = order;

            let orderLine = await db
              .doc(`orders/${orderRef.id}/line_items/${LineNum}`)
              .get();
            orderLine = orderLine.data();
            if (orderLine.Items.length > 1) {
              this.openQC(orderLine.Items[0]);
            } else {
              this.handleMultipleItemsQA(orderLine.Items);
            }
          } else {
            this.handleViewModal(order);
          }
        }
      }
    },

    handleBarcodeScanned(e) {
      if (e.target.id != "search-input") {
        if (e.key != "Shift" && e.key != "Enter" && e.key !== "Escape") {
          this.barcodeValue += e.key;
        }
      }
    },

    handleReportSearch(value) {
      let allOrderItemsArray = Object.values(this.allOrderItemsInfo);
      this.searchValueForAllOrderItems = allOrderItemsArray.filter((i) =>
        this.selectedSearchByForAllOrderItems == "LineNum"
          ? i[this.selectedSearchByForAllOrderItems] == value
          : i[this.selectedSearchByForAllOrderItems]?.includes(value)
      );
    },
    // sorting helpers
    sortBy(field) {
      if (this.currentSort === field) {
        // toggle direction
        this.currentSortOrder =
          this.currentSortOrder === "asc" ? "desc" : "asc";
      } else {
        this.currentSort = field;
        this.currentSortOrder = "asc";
      }
    },

    sortClass(field) {
      if (this.currentSort !== field) return "";
      return this.currentSortOrder === "asc" ? "sorted-asc" : "sorted-desc";
    },
    rowKey(item) {
      if (item) return item.id || item.OrderNum;
      else return "";
      // Use any unique id you have; fallback is combo
    },

    isRowExpanded(item) {
      return !!this.expandedRows[this.rowKey(item)];
    },

    toggleRow(item) {
      const key = this.rowKey(item);
      this.selectedItemToExpand = item;
      this.$set(this.expandedRows, key, !this.expandedRows[key]);
    },
    closeExpandedRow() {
      const key = this.rowKey(this.selectedItemToExpand);
      this.selectedItemToExpand = null;
      this.$delete(this.expandedRows, key, false);
    },
    resetTable() {
      this.pagination.currentPage = 1;
      this.searchedData = [];
      this.fetchData("start");
    },

    // Helper to safely unsubscribe from active real-time listeners to avoid memory leaks
    unbindListener() {
      if (typeof this._unsubscribe === 'function') {
        this._unsubscribe();
        this._unsubscribe = null;
      }
    },

    async fetchData(action) {
      if (this.searchedData.length) return false;
      let loader = this.$loading.show({
        container: null,
        canCancel: false,
      });

      try {
        this.unbindListener(); // Clear any existing listener before starting a new query

        let querySnapshot = null;

        if (action == "start") {
          querySnapshot = this.ordersRef
            .orderBy("Timestamp", "desc")
            .limit(this.pagination.perPage);

        } else if (action == "next") {
          let lastDoc = this.tableData[this.tableData.length - 1];
          if (lastDoc) {
            lastDoc = await this.ordersRef.doc(lastDoc.id).get();
          }
          querySnapshot = this.ordersRef
            .orderBy("Timestamp", "desc")
            .startAfter(lastDoc)
            .limit(this.pagination.perPage);

        } else if (action == "prev") {
          let firstDoc = this.tableData[0];
          if (firstDoc) {
            firstDoc = await this.ordersRef.doc(firstDoc.id).get();
          }
          // For previous pagination, we query ascending and then reverse the results array
          const snapshot = await this.ordersRef
            .orderBy("Timestamp", "asc")
            .startAfter(firstDoc)
            .limit(this.pagination.perPage)
            .get();

          this.tableData = snapshot.docs
            .map((doc) => Object.assign({ id: doc.id }, doc.data()))
            .reverse();
          loader.hide();
          return;

        } else if (action == "per-page") {
          let firstDoc = this.tableData[0];
          if (firstDoc) {
            firstDoc = await this.ordersRef.doc(firstDoc.id).get();
          }
          querySnapshot = this.ordersRef
            .orderBy("Timestamp", "desc")
            .startAt(firstDoc)
            .limit(this.pagination.perPage);
        }

        // Attach a real-time listener using onSnapshot so updates reflect instantly
        if (querySnapshot) {
          this._unsubscribe = querySnapshot.onSnapshot((snapshot) => {
            this.tableData = snapshot.docs.map((doc) =>
              Object.assign({ id: doc.id }, doc.data())
            );
            loader.hide();
          }, (error) => {
            console.error("Error with real-time listener:", error);
            loader.hide();
          });
        } else {
          loader.hide();
        }

      } catch (error) {
        console.error("Error fetching data:", error);
        loader.hide();
      }
    },

    // Handlers map cleanly into your main fetch controller
    async fetchNextData() {
      await this.fetchData("next");
    },

    async fetchPreviousData() {
      await this.fetchData("prev");
    },


    async getQAAssociate() {
      let allQaAssociates = await db.doc("attributes/qa_associates").get();
      allQaAssociates = allQaAssociates.data().employees;
      this.allQaAssociates = allQaAssociates;
    },

    onSelected(data) {
      this.selectedExportData = data;
    },

    handleNoteModal(item, id) {
      this.showNoteModal = true;
      this.selectedItem = item;
      this.selectedItem.id = id;
    },

    closeNoteModal() {
      this.showNoteModal = false;
      this.selectedItem = {};
    },

    closeImageModal() {
      this.showItemImagesModal = false;
      this.selectedFailedItem = {};
      this.selectedItemType = null;
    },

    handleSaveNote() {
      this.allOrderItemsInfo[this.selectedItem.id || this.selectedItem.ItemNum].Responses.notes =
        this.selectedItem.Responses.notes;

      db.doc(
        `orders/${this.selectedItem.OrderNum}/quality_control/${this.selectedItem.id || this.selectedItem.ItemNum}`
      )
        .set({ notes: this.selectedItem.Responses.notes, rejections: [] })
        .then(async () => {
          const order = this.selectedOrder;
          this.snackbarMsg = "Note has been saved";
          this.showSnackbar = true;
          this.closeNoteModal();
          this.closeReportModal();
          this.showFailedItemReport(order);
        });
    },
    // Make sure ExcelJS is available (via import or script tag)
    // import ExcelJS from "exceljs";

    async exportCSV(action) {
      const format_date = (v) => moment(v).format("MM/DD/YYYY");
      const loader = this.$loading.show({ container: null, canCancel: false });

      try {
        const { selectedExportData, searchedData, tableData } = this;
        const payload =
          action === "all"
            ? searchedData.length > 0
              ? searchedData
              : tableData
            : selectedExportData;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("QA Report");

        sheet.columns = [
          { header: "Date", key: "date", width: 12 },
          { header: "Order", key: "order", width: 16 },
          { header: "Venue", key: "venue", width: 18 },
          { header: "Purchase Orders", key: "purchaseOrders", width: 20 },
          { header: "SKU", key: "sku", width: 18 },
          { header: "Certificate", key: "certificate", width: 18 },
          { header: "Status", key: "status", width: 14 },
          { header: "Rejections", key: "rejections", width: 40 },
          { header: "Images", key: "images", width: 20 },
        ];

        // Helper to process a single order
        const processOrder = async (_order) => {
          const orderID = _order.id;
          const orderTimeStamp = _order.Timestamp;

          // Define the items query based on filters
          let itemsQuery = db.collection(`orders/${orderID}/items`);
          if (this.filterBy.showFailedItems) {
            itemsQuery = itemsQuery.where("Status", "==", "Failed");
          } else if (this.filterBy.showPassedWithRepair) {
            itemsQuery = itemsQuery.where("Status", "==", "Passed-W/Repair");
          } else if (this.filterBy.showCertificateOrders) {
            itemsQuery = itemsQuery.where("CertNum", "!=", "");
          }

          // FETCH EVERYTHING FOR THIS ORDER IN PARALLEL
          const [orderSnap, itemsSnap, qaSnap] = await Promise.all([
            db.doc(`orders/${orderID}`).get(),
            itemsQuery.get(),
            db.collection(`orders/${orderID}/quality_control`).get(),
          ]);

          const orderData = orderSnap.data();
          const qaNotesMap = {};

          // Map QA notes
          qaSnap.forEach((doc) => {
            const qaInfo = doc.data();
            qaNotesMap[doc.id] = qaInfo?.rejections?.length
              ? qaInfo.rejections.join(" ")
              : qaInfo?.notes?.replace(/[\r\n]/gm, "") || "";
          });

          // Map Items
          return itemsSnap.docs.filter(d => !d.id.includes('R'))
            .map((doc) => {
              const item = doc.data();
              const certOrItem = item.CertNum || item.ItemNum || "";
              return {
                date: format_date(orderTimeStamp),
                order: `${item.OrderNum}-${item.LineNum}`,
                venue: (item.Customer || "").replace(",", "."),
                purchaseOrders: item.PO || orderData?.PurchaseOrders?.[0] || "",
                sku: item.SKU || item.ClientSKU || "",
                certificate: certOrItem,
                status: item.Status || "",
                rejections: qaNotesMap[doc.id] || "",
                imageLabel: certOrItem,
                imageUrl: item.Images?.length ? item.Images[0]?.url : "",
                lineNum: item.LineNum, // for sorting
              };
            })
            .sort((a, b) => a.lineNum - b.lineNum);
        };

        // --- CHUNKED EXECUTION ---
        // Process in batches of 50 to avoid browser/network throttle
        const batchSize = 50;
        let allRows = [];

        for (let i = 0; i < payload.length; i += batchSize) {
          const batch = payload.slice(i, i + batchSize);
          const batchResults = await Promise.all(
            batch.map((order) => processOrder(order))
          );
          allRows.push(...batchResults.flat());
        }

        // Add rows to Excel
        allRows.forEach((r) => {
          const row = sheet.addRow(r);
          row.getCell("images").value =
            r.imageLabel || (r.imageUrl ? "View Image" : "");

          if (r.imageUrl) {
            const cell = row.getCell("images");
            cell.value = { text: cell.value, hyperlink: r.imageUrl };
            cell.font = { color: { argb: "FF0563C1" }, underline: true };
          }
        });

        // Formatting & Download
        sheet.views = [{ state: "frozen", ySplit: 1 }];
        sheet.getRow(1).font = { bold: true };

        const excelBuffer = await workbook.xlsx.writeBuffer({
          useSharedStrings: true,
        });
        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `QA Report ${moment().format("LL")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        loader.hide();
        console.error("Excel export failed:", err);
        alert("Export failed. Check console for details.");
      } finally {
        loader.hide();
        this.showExportOptions = false;
        this.showSelectableTable = false;
        this.selectedExportData = [];
      }
    },

    removeImage(data, index) {
      this.selectedFailedItem.Images.splice(index, 1);
      this.$forceUpdate();
      db.doc(
        `orders/${this.selectedFailedItem.OrderNum}/items/${this.selectedFailedItem.ItemNum}`
      ).update({
        Images: this.selectedFailedItem.Images,
      });
    },

    uploadedImages(data) {
      if (this.selectedItemType === "failed") {
        return this.uploadImageForFailedItem(data);
      } else if (this.selectedItemType == "individual") {
        return this.uploadImageForIndividualItem(data);
      }
    },

    async uploadImageForFailedItem(data) {
      db.doc(
        `orders/${this.selectedFailedItem.OrderNum}/items/${this.selectedFailedItem.id}`
      )
        .update(
          Object.assign(
            {
              Images: firebase.firestore.FieldValue.arrayUnion(data),
            },
          )
        )
        .then(() => {
          const order = this.selectedOrder;
          this.snackbarMsg = "Image Uploaded Successfully";
          this.showSnackbar = true;
          // this.closeImageModal();
          // this.closeReportModal();
          this.showFailedItemReport(order);
        })
        .catch((err) => {
          this.snackbarMsg = `Error has occurred ${err.message}. Try again later or contact support`;
          this.showSnackbar = true;
        });
      this.selectedFailedItem.Images.push(data);
    },

    uploadImageForIndividualItem(data) {
      db.doc(
        `orders/${this.selectedItem.OrderNum}/items/${this.selectedItem.id || this.selectedItem.ItemNum}`
      )
        .update({ ImageURL: data.url })
        .then(() => {
          this.selectedItem.ImageURL = data.url;
          this.snackbarMsg = "Image Uploaded Successfully";
          this.showSnackbar = true;
          this.closeImageModal();
        })
        .catch((err) => {
          this.snackbarMsg = `Error has occurred ${err.message}. Try again later or contact support`;
          this.showSnackbar = true;
        });
    },

    saveDescription(item) {
      return db
        .doc(`orders/${this.selectedOrder.OrderNum}/line_items/${item.LineNum}`)
        .update({ Description: item.Description })
        .then(() => {
          this.showChangesBtn = false;
          this.snackbarMsg = "Changes has been saved successfully";
          this.showSnackbar = true;
        })
        .catch((err) => {
          this.snackbarMsg = `Error has occurred ${err.message}. Try again later or contact support`;
          this.showSnackbar = true;
        });
    },

    goBack(cur, prev) {
      this[cur] = false;
      this[prev] = true;
    },

    handleImageModal(url) {
      this.imageView = url;
      this.showImageModal = true;
    },

    handleSearchForIndividualItem({ searchValue, searchKey }) {
      const value = searchValue;
      if (!value) return (this.allLineItems = this.allLineItemsUnfiltered);
      this.allLineItems = this.allLineItemsUnfiltered.filter((i) =>
        searchKey == "LineNum"
          ? i[searchKey] == value
          : i[searchKey].includes(value)
      );
    },

    saveQC(qaItem, qcResponses, save4all) {
      let item = qaItem || this.allFailedItems[this.popupTab];
      const { OrderNum, ItemNum } = item
      qcResponses.createdBy = this.activeUser.uid;
      return db
        .doc(`orders/${OrderNum}/quality_control/${ItemNum}`)
        .set(qcResponses, { merge: true })
        .then(() => save4all ? null : this.changeItemTab(this.popupTab + 1));
    },

    finishQCProtocol() {
      this.popupTab = null
      this.showQCModalForMult = false;
      if (this.isBeingHandledThroughAuthWindow) {
        if (this.$store.state.user.isSuperAdmin) {
          this.showQAAssociateModal = true;
        } else
          this.saveAuthChanges()
      }
    },

    changeItemTab(tab) {
      this.popupTab = null;
      setTimeout(() => {
        this.popupTab = tab;
      }, 500);
      return true;
    },

    closeReportModal() {
      this.selectedOrder = {};
      this.allOrderItemsInfo = {};
      this.showOrderReportModal = false;
      this.searchValueForAllOrderItems = [];
      this.allOrderLineInfo = {};
      this.tableTab = 'Line Item Status'
    },
    getUniqueDefects(arr) {
      // Trim whitespace from each item and filter out duplicates using a Set
      const cleaned = arr.map(item => item.trim().replace(/[.,]/g, ''));
      return [...new Set(cleaned)];
    },
    async showFailedItemReport(item) {
      let loader = this.$loading.show({
        container: null,
        canCancel: false,
      });

      this.selectedOrder = item;
      const failedItemsPerLine = {}
      const skippedItemsPerLine = {}
      const repairedItemsPerLine = {}
      let allOrderItems = await db
        .collection(`orders/${item.OrderNum}/items`)
        .get();
      let responses = await db
        .collection(`orders/${item.OrderNum}/quality_control`)
        .get();

      let allOrderLineItems = await db
        .collection(`orders/${item.OrderNum}/line_items`)
        .get();



      for (let index = 0; index < allOrderItems.docs.length; index++) {
        const d = allOrderItems.docs[index];
        const item = d.data()
        this.allOrderItemsInfo[d.id] = item
        this.allOrderItemsInfo[d.id]["Responses"] = {
          notes: "",
          rejections: [],
        };

        if (item.Status == 'Failed') {
          if (!failedItemsPerLine[item.LineNum]) {
            failedItemsPerLine[item.LineNum] = 0
          }
          failedItemsPerLine[item.LineNum] += 1
        }

        if (item.Status == 'Skipped') {
          if (!skippedItemsPerLine[item.LineNum]) {
            skippedItemsPerLine[item.LineNum] = 0
          }
          skippedItemsPerLine[item.LineNum] += 1
        }

        if (item.Status == 'Passed-W/Repair') {
          if (!repairedItemsPerLine[item.LineNum]) {
            repairedItemsPerLine[item.LineNum] = 0
          }
          repairedItemsPerLine[item.LineNum] += 1
        }


      }


      for (let index = 0; index < allOrderLineItems.docs.length; index++) {
        const d = allOrderLineItems.docs[index];
        this.allOrderLineInfo[d.id] = d.data();
        this.allOrderLineInfo[d.id].failedQty = failedItemsPerLine[d.id]
        this.allOrderLineInfo[d.id].skippedQty = skippedItemsPerLine[d.id]
        this.allOrderLineInfo[d.id].repairedQty = repairedItemsPerLine[d.id]
        this.allOrderLineInfo[d.id].Responses = []
        this.allOrderLineInfo[d.id].Images = []
        this.allOrderLineInfo[d.id].Notes = []
      }
      responses.docs.forEach((d) => {
        let response = d.data();
        if (this.allOrderItemsInfo[d.id]) {
          this.allOrderItemsInfo[d.id].Responses = response
          const lineNum = this.allOrderItemsInfo[d.id].LineNum
          if (this.allOrderLineInfo[lineNum]) {
            this.allOrderLineInfo[lineNum].Responses.push(response)
            this.allOrderLineInfo[lineNum].Notes = [].concat(...this.allOrderLineInfo[lineNum].Notes, response.notes)
            if (this.allOrderItemsInfo[d.id] && this.allOrderLineInfo[lineNum]) {
              this.allOrderItemsInfo[d.id].Images.forEach(image => {
                const foundImage = this.allOrderLineInfo[lineNum].Images.find(i => i.name == image.name)
                if (!foundImage) {
                  this.allOrderLineInfo[lineNum].Images.push(image)
                }
              })
            }
          }
        }
      });

      this.showOrderReportModal = true;
      loader.hide();
    },

    closeAuthModal() {
      this.allItemsForAuthentication = [];
      this.showAuthModal = false;
      this.searchAuthItems = null;
      this.showQAAssociateModal = false;
      this.selectedAuthItems = []
      this.statusForAllItems = null
    },

    endAuthSequence() {
      this.allItemsForAuthentication = [];
      this.selectedAuthItems = []
      this.statusForAllItems = null
      this.searchAuthItems = null;
      this.showAuthModal = false;
      this.showQAAssociateModal = false;
      if (this.$store.state.user.isSuperAdmin) this.selectedQaAssociate = null;
    },

    handleStatuses(e, status) {
      const isChecked = e.target.checked
      if (isChecked) {
        this.statusForAllItems = status
        this.allItemsForAuthentication.forEach((i) => {
          if (!i.QCCompleted) {
            i.Status = status;
            this.handleSelectedAuthItem(i, 'add')
          }
        });
      } else {
        this.statusForAllItems = null
        this.allItemsForAuthentication.forEach((i) => {
          if (!i.QCCompleted) {
            i.Status = "Needs Q/A";
            this.handleSelectedAuthItem(i, 'remove')
          }
        });
      }



    },
    handleSelectedAuthItem(item, action) {
      if (action == 'add') {
        if (!item.QCCompleted) {
          const itemExists = this.selectedAuthItems.find(i => i.id == item.id)
          if (!itemExists) this.selectedAuthItems.push(item)
          else {
            const itemIndex = this.selectedAuthItems.findIndex(i => i.id == item.id)
            this.selectedAuthItems[itemIndex] = item
          }
        } else {
          Swal.fire({
            title: "Are you sure?",
            text: "You are changing the status of an item that has been completed. You sure you want to make this change?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes!",
          }).then(async (result) => {
            if (result.value) {
              item.QCCompleted = false
              item.QACompletedAt = null
              item.QA_Associate = null
              this.selectedAuthItems.push(item)
            }
          });
        }

      } else {
        this.selectedAuthItems = this.selectedAuthItems.filter(i => i.id !== item.id)
      }

    },
    handleAuthChanges() {
      let allItemsForAuthentication = this.allItemsForAuthentication;
      let allFailedItemsThatWereNotQA = allItemsForAuthentication.filter(
        (i) => i.Status === "Failed" && !i.QCCompleted
      );

      let allFailedItemsThatWereQA = allItemsForAuthentication.filter(
        (i) => i.Status === "Failed" && i.QCCompleted
      );

      let allPassedItems = allItemsForAuthentication.filter(
        (i) => i.Status !== "Failed"
      );

      if (allFailedItemsThatWereNotQA.length) {
        return Swal.fire(
          "QC Processing",
          "Failed Items Must Go To QC Processing First. Click Ok To QC Failed Items",
          "warning"
        ).then(() => this.handleMultipleItemsQA(allFailedItemsThatWereNotQA));
      }

      if (allFailedItemsThatWereQA.length || allPassedItems.length) {
        if (this.$store.state.user.isSuperAdmin)
          this.showQAAssociateModal = true;
        else this.saveAuthChanges();
      }
    },

    handleMultipleItemsQA(data) {
      this.allFailedItems = data.map((i) => {
        i.qcResponses = JSON.parse(
          JSON.stringify(Object.assign({}, this.qcResponses))
        );
        return i;
      });
      this.showAuthModal = false;
      this.searchAuthItems = null;
      this.popupTab = 0;
      this.isBeingHandledThroughAuthWindow = true
      this.showQCModalForMult = true;
    },

    async saveAuthChanges() {
      // 1. Guard Clause: Stop early if data is missing
      if (!this.selectedQaAssociate) {
        return Swal.fire(
          "Select QA Associate",
          "Please select QA Associate before moving forward",
          "error"
        );
      }

      const loader = this.$loading.show({ container: null, canCancel: false });

      try {

        const batch = db.batch();
        const orderId = this.allItemsForAuthentication[0]?.OrderNum;

        if (!orderId) throw new Error("No order data found.");

        // 2. Efficient Batching
        // Instead of individual 'await' updates, we add them to one atomic batch
        this.selectedAuthItems.forEach((item) => {
          const itemRef = db.doc(`orders/${orderId}/items/${item.id}`);
          batch.update(itemRef, {
            Status: item.Status,
            UserID: this.activeUser.uid,
            QA_Associate: this.selectedQaAssociate,
            QCCompleted: true,
            QACompletedAt: Date.now()
          });
        });

        // Commit DB changes and API calls simultaneously
        await Promise.all([batch.commit()]);
        const payload = this.selectedAuthItems;

        // 5. Success UI
        Swal.fire(
          "Authenticated",
          `Order #${orderId} has been authenticated`,
          "success"
        );
        this.sendDataToCertificateDB(payload, orderId);
        this.endAuthSequence();
      } catch (error) {
        console.error(error);
        Swal.fire("Error", error.message, "error");
      } finally {
        loader.hide();
      }
    },

    sendDataToCertificateDB(payload, orderId) {
      this.certificateAPI(payload).catch((err) =>
        console.error(`Order ${orderId} failed:`, err)
      );
    },

    closeTableModal() {
      this.selectedOrderLineStatus = [];
      this.allLineItems = [];
      this.allLineItemsUnfiltered = [];
      this.selectedOrder = {};
      this.allItemsForSelectedLine = [];
      this.allFailedItems = [];
      this.selectedLineItem = {};
      this.selectedItem = {};
      this.showDialog = false;
    },

    clearFilters() {
      for (const key in this.filterBy) {
        if (key === "client" || key === "status") {
          this.filterBy[key] = "";
        } else if (key === "startDate" || key === "endDate") {
          this.filterBy[key] = null;
          // this.filterBy[key] = "";
        } else if (
          key === "showFailedItems" ||
          key === "showPassedWithRepair" ||
          key === "showPassedItems" ||
          key === "showShippedItems"
        ) {
          this.filterBy[key] = false;
        } else {
          this.filterBy[key] = "";
        }
      }
      this.searchVal = "";
      this.resetTable();
    },

    async handleAuthSearch() {
      let value = this.searchAuthItems.trim();
      this.allItemsForAuthentication = [];
      this.selectedAuthItems = []
      let orderRef;
      this.orderBeingAuthenticated = value;
      if (value.includes("-")) {
        value = value.split("-");
        const orderNum = value[0].trim();
        const lineNum = Number(value[1]);
        this.orderBeingAuthenticated = orderNum;
        orderRef = db
          .collection(`orders/${orderNum}/items`)
          .where("LineNum", "==", lineNum);
      } else {
        orderRef = db.collection(`orders/${value}/items`);
      }

      const fetchedDocs = await orderRef.orderBy("LineNum", 'asc').get();

      if (fetchedDocs.empty) {
        return Swal.fire("Empty Search Records");
      }

      this.allItemsForAuthentication = fetchedDocs.docs.map((d) =>
        Object.assign({ id: d.id }, d.data())
      );
    },

    async handleSearch(e) {
      // 1. Early Exit & Keyboard check
      if (e.keyCode !== 13 && e !== "click") return;

      const { selectedSearchBy } = this;
      let { searchVal } = this;
      searchVal = searchVal.replace(/\s/g, "")
      if (!searchVal) {
        this.clearFilters();
        return;
      }

      try {
        let query = db.collection("orders");

        // 1. Handle specific search types

        if (selectedSearchBy === "on") {
          // Order Number specific formatting
          if (searchVal.includes("-")) searchVal = searchVal.split("-")[0];

          // If searching by ID, it's more efficient to use .doc() directly
          // ONLY if there are no other 'where' constraints.
          // Otherwise, use .where("__name__", "==", id) to keep the query filters.
          query = query.where("__name__", "==", searchVal);
        } else if (selectedSearchBy === "po") {
          query = query.where("PurchaseOrders", "array-contains", searchVal);
        } else if (selectedSearchBy === "sku") {
          query = query.where("ItemSKUs", "array-contains", searchVal);
        } else if (selectedSearchBy === "cert") {
          query = query.where("ItemCerts", "array-contains", searchVal);
        }

        // 4. Execution
        const snapshot = await query.orderBy("Timestamp", 'desc').get();

        // 5. Data Mapping
        if (snapshot.empty) {
          Swal.fire("", "No Records Have Been Found", "error");
          this.tableData = [];
        } else {
          // Handles both query snapshots and single doc results (via where name)
          this.tableData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        }
      } catch (error) {
        console.error("Search Error:", error);
        Swal.fire("Error", "An error occurred while fetching data", "error");
      }
    },

    async handleFilter() {
      let container = { docs: [] };
      let loader = this.$loading.show({
        container: null,
        canCancel: false,
      });
      let ref = db.collection("orders");

      if (
        !this.filterBy.showFailedItems &&
        !this.filterBy.showPassedWithRepair &&
        !this.filterBy.showCertificateOrders &&
        !this.filterBy.showPassedItems &&
        !this.filterBy.showShippedItems &&
        !this.filterBy.customers &&
        !this.filterBy.client &&
        !this.filterBy.status &&
        !this.filterBy.startDate &&
        !this.filterBy.endDate
      ) {
        this.resetTable();
        loader.hide();
        return false;
      }

      try {
        for (const key in this.filterBy) {
          if (Object.hasOwnProperty.call(this.filterBy, key)) {
            if (key != "startDate" && key != "endDate") {
              const element = this.filterBy[key];
              if (element) {
                if (key == "client") {
                  ref = ref.where("Client", "==", element);
                }

                if (key == "customers") {
                  ref = ref.where("Customer", "==", element);
                }

                if (key == "status") {
                  ref = ref.where("Status", "==", element);
                }

                if (key == "showCertificateOrders") {
                  ref = ref.where(
                    "ServiceType",
                    "==",
                    "Screening,QA,Certificate,DropShip"
                  );
                }

                if (key == "showPassedWithRepair") {
                  ref = ref
                    .orderBy("PassedWithRepairsItems", "asc")
                    .where("PassedWithRepairsItems", ">", 0);
                }

                if (key == "showFailedItems") {
                  ref = ref
                    .orderBy("FailedItems", "asc")
                    .where("FailedItems", ">", 0);
                }

                if (key == "showShippedItems") {
                  ref = ref
                    .orderBy("ShippedItemsQty", "asc")
                    .where("ShippedItemsQty", ">", 0);
                }


              }
            }
          }
        }

        if (
          this.filterBy.startDate &&
          this.filterBy.endDate &&
          !this.filterBy.showFailedItems &&
          !this.filterBy.showShippedItems &&
          !this.filterBy.showPassedWithRepair &&
          !this.filterBy.showCertificateOrders &&
          !this.filterBy.showPassedItems &&
          !this.filterBy.customers &&
          !this.filterBy.client &&
          !this.filterBy.status
        ) {
          let startDateObj =
            typeof this.filterBy.startDate == "object"
              ? DateTime.fromJSDate(this.filterBy.startDate)
              : DateTime.fromISO(this.filterBy.startDate);

          let endDateObj =
            typeof this.filterBy.endDate == "object"
              ? DateTime.fromJSDate(this.filterBy.endDate)
              : DateTime.fromISO(this.filterBy.endDate);

          let startDate = startDateObj
            .setZone("America/New_York")
            .startOf("day")
            .toMillis();

          let endDate = endDateObj
            .setZone("America/New_York")
            .endOf("day")
            .toMillis();

          ref = ref
            .orderBy("Timestamp", "desc")
            .startAt(endDate)
            .endAt(startDate);
          // .startAt(startDate)
          // .endAt(endDate);

          container = await ref.get();

          if (!container.docs.length) {
            loader.hide();
            return Swal.fire("Empty Search Records");
          }
          this.searchedData = container.docs.map((d) =>
            Object.assign({ id: d.id }, d.data())
          );
        } else if (
          this.filterBy.startDate &&
          this.filterBy.endDate &&
          (this.filterBy.showFailedItems ||
            this.filterBy.showShippedItems ||
            this.filterBy.showPassedWithRepair ||
            this.filterBy.showCertificateOrders ||
            this.filterBy.showPassedItems ||
            this.filterBy.customers ||
            this.filterBy.client ||
            this.filterBy.status)
        ) {
          let startDateObj =
            typeof this.filterBy.startDate == "object"
              ? DateTime.fromJSDate(this.filterBy.startDate)
              : DateTime.fromISO(this.filterBy.startDate);

          let endDateObj =
            typeof this.filterBy.endDate == "object"
              ? DateTime.fromJSDate(this.filterBy.endDate)
              : DateTime.fromISO(this.filterBy.endDate);

          let startDate = startDateObj
            .setZone("America/New_York")
            .startOf("day")
            .toMillis();

          let endDate = endDateObj
            .setZone("America/New_York")
            .endOf("day")
            .toMillis();

          container = await ref.get();
          if (!container.docs.length) {
            loader.hide();
            return Swal.fire("Empty Search Records");
          }
          this.searchedData = container.docs
            .filter(
              (doc) =>
                doc.data().Timestamp >= startDate &&
                doc.data().Timestamp <= endDate
            )
            .map((d) => Object.assign({ id: d.id }, d.data()))
            .sort((a, b) => b.Timestamp - a.Timestamp);
        } else {
          container = await ref.get();
          if (!container.docs.length) {
            loader.hide();
            return Swal.fire("Empty Search Records");
          }

          this.searchedData = container.docs
            .map((d) => Object.assign({ id: d.id }, d.data()))
            .sort((a, b) => b.Timestamp - a.Timestamp);
        }

        loader.hide();
      } catch (error) {
        console.error(error);
        loader.hide();
        return Swal.fire(
          "Internal Issue",
          `Oops there seems to be an issue please try again or contact DIS support`,
          "warning"
        );
      }
    },

    validateDateRange(key) {
      if (moment(this.filterBy.startDate).isAfter(this.filterBy.endDate)) {
        this.filterBy.endDate = null;
      }

      if (key === "startDate") {
        this.filterBy.endDate = this.filterBy.startDate;
      }
    },

    async openQC(item) {
      this.showModalContentForItem = false;
      if (item) this.selectedItem = item;

      let qcResponsesRef = await db
        .doc(
          `orders/${this.selectedOrder.id}/quality_control/${this.selectedItem.ItemNum}`
        )
        .get();

      if (qcResponsesRef.exists) {
        this.qcResponses = qcResponsesRef.data();

      }
      this.showQCModal = true;

    },

    clearQCResponses() {
      this.showQCModal = false;
      this.leftQCPanelInit = false;
      this.qcResponses = {
        rejections: [],
      };
    },

    validateQCResponses(val) {
      let valid = false;

      if (val.rejections.length || this.leftQCPanelInit || val.notes) {
        valid = true;
      }

      return valid;
    },

    handleQCChanges() {
      this.qcResponses.createdBy = this.activeUser.uid;
      if (this.validateQCResponses(this.qcResponses))
        return db
          .doc(
            `orders/${this.selectedOrder.id}/quality_control/${this.selectedItem.ItemNum}`
          )
          .set(this.qcResponses, { merge: true })
          .then(() => {
            const certificateAPI = functions.httpsCallable(
              "certificateAPI-updateCertificate"
            );

            try {
              return certificateAPI({
                status: this.selectedItem.Status,
                certNum: this.selectedItem.CertNum,
                stockNum: this.selectedItem.ItemNum,
                trackingNum: this.selectedItem.TrackingNum || "",
              });
            } catch (error) {
              console.log(error);
              return true;
            }
          })
          .then(() => {
            this.clearQCResponses();
          });
      else
        return Swal.fire(
          "",
          "No changes has been detected, cannot continue without proper Q/A",
          "error"
        );
    },

    selectedLineItemAction(item) {
      this.showLineItemModal = true;
      this.selectedLineItem = item;
      this.allItemsForSelectedLine = this.allLineItems.filter(
        (i) => i.LineNum == item.LineNum
      );
      this.showModalContentForLineItem = true;
      this.showModalContentForItem = false;
      this.showDialog = false;
    },

    selectedItemAction(item) {
      this.showLineItemModal = false;
      this.selectedItem = item;
      this.showModalContentForLineItem = false;
      this.showModalContentForItem = true;
      this.showDialog = false;
    },

    handleViewModal(item) {
      this.selectedOrder = item;
      this.selectedOrderLineStatus = item.LineItems;
      this.$bind(
        "allLineItemsUnfiltered",
        db.collection(`orders/${item.id}/items`)
      );
      this.allLineItems = this.allLineItemsUnfiltered;
      this.showDialog = true;
    },

    handleAuthViewModal(item) {
      this.selectedOrder = item;
      this.selectedOrderLineStatus = item.LineItems;
      this.$bind(
        "allLineItemsUnfiltered",
        db.collection(`orders/${item.id}/items`)
      );
      this.allLineItems = this.allLineItemsUnfiltered;
      this.showAuthViewModal = true;
    },

    closeAuthViewModal() {
      this.selectedOrder = {};
      this.selectedOrderLineStatus = [];
      this.showAuthViewModal = false;
    }
  }
};
</script>

<style scoped>
/* ===== Layout shell ===== */

.md-layout {
  display: flex;
  justify-content: center;
  background: #f5f4f8;
}

.md-layout-item {
  max-width: 90vw;
  margin: 24px auto 40px;
  position: relative;
}

/* ===== Generic QC card styling ===== */

.qc-card {
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
  margin-bottom: 16px;
}

.qc-search-card {
  padding-top: 4px;
}

.qc-filters-card {
  margin-bottom: 20px;
}

.qc-table-card {
  padding-top: 4px;
}

.qc-2 {
  height: 460px !important;
  overflow: auto;
  display: block;
}

/* ===== Search row / radios / input ===== */

.qc-search-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  align-items: end;
}

.qc-search-left {
  min-width: 220px;
}

.qc-search-label {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
}

/* pill toggle for search-by radios */

.search-options ul {
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: #f3f4f6;
  list-style: none;
  margin: 0;
}

.search-options li {
  position: relative;
  margin: 0;
}

.search-options input[type="radio"] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.search-options span {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  color: #4b5563;
}

.search-options input[type="radio"]:checked+span {
  background: #253c6e;
  color: #f9fafb;
}

/* search input */

.qc-search-input-wrapper {
  flex: 1;
  min-width: 260px;
}

.qc-search-field {
  margin: 0;
}

.qc-search-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ===== Buttons ===== */

.qc-btn {
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  text-transform: none;
}

.qc-btn-primary {
  background: #253c6e !important;
  color: #f9fafb !important;
}

.qc-btn-primary:hover {
  background: #020617 !important;
}

.qc-btn-secondary {
  background: #e5e7eb !important;
  color: #253c6e !important;
}

/* ===== Filters grid ===== */

.filters {
  display: flex;
  row-gap: 18px;
  align-items: flex-end;
  column-gap: 6%;
}

.filter p {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
}

.filter.customer {
  min-width: 200px;
}

.filter-status {
  grid-column: span 2;
}

.status-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  align-items: center;
  font-size: 12px;
}

.filter-btn {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.bottom-row {
  padding-right: 10px;
  padding-left: 10px;
  display: flex;
  justify-content: space-between;
  align-items: end;
}

/* ===== Table card / Export row ===== */

.expand-toggle {
  margin-left: 4px;
}

.qc-details-row {
  background: #f9fafb;
}

.qc-details {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 8px 4px 4px;
}

.qc-detail-group {
  min-width: 180px;
  max-width: 280px;
}

.qc-detail-group h4 {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.qc-detail-group p {
  margin: 0;
  font-size: 12px;
  color: #253c6e;
}

.qc-details .item-sku-wrapper {
  max-width: 100%;
}

.qc-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
}

.btn-row.stats_holder {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.qc-export-options {
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* wrapper so table can scroll horizontally if needed */

.qc-table-wrapper {
  max-height: 400px;
  margin-top: 8px;
  overflow-x: auto;
}

/* ===== Table look & feel ===== */
.qc-more-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  margin-left: 4px;
  border-radius: 999px;
  font-size: 10px;
  background: #e5e7eb;
  color: #4b5563;
  white-space: nowrap;
}

.qc-table {
  font-size: 12px;
}

.qc-table thead {
  background: #f9fafb;
}

.qc-table thead tr th {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
  color: white;
}

/* zebra / hover */

.qc-table tbody tr:nth-child(even) {
  background: #fafafa;
}

.qc-table tbody tr:hover {
  background: #f3f4ff;
}

/* Base table look */
.qc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #ffffff;
}

.qc-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f9fafb;
}

.qc-table thead th {
  text-align: left;
  padding: 8px 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
  color: white;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  white-space: nowrap;
}

.qc-table thead th:last-child {
  text-align: center;
  cursor: default;
  /* Controls header not sortable */
}

.qc-table tbody tr.qc-main-row td {
  padding: 8px 10px;
  /* border-bottom: 1px solid #f1f5f9; */
}

/* zebra + hover */
.qc-table tbody tr.qc-main-row:nth-child(4n + 1),
.qc-table tbody tr.qc-main-row:nth-child(4n + 2) {
  background: #ffffff;
}

.qc-table tbody tr.qc-main-row:nth-child(4n + 3),
.qc-table tbody tr.qc-main-row:nth-child(4n) {
  background: #fafafa;
}

.qc-table tbody tr.qc-main-row:hover {
  background: #f3f4ff;
}

.qc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  background: #fff;
}

.qc-table thead th {
  padding: 10px;
  text-transform: uppercase;
  color: white;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
}

.qc-main-row td {
  padding: 10px;
  /* border-bottom: 1px solid #f1f5f9; */
}

.qc-main-row:hover {
  background: #f3f4ff;
}

.qc-controls {
  text-align: right;
  white-space: nowrap;
}

.expand-btn {
  margin-left: 4px;
}

.data-list {
  max-height: 200px;
  width: 250px;
}

.data-list>ul {
  text-align: left;
  max-height: 200px;
  overflow: auto;
  max-width: 250px;
  border: solid black 0.5px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 5px;
}

.data-list>ul>li {
  width: 85px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- DETAILS DROPDOWN --- */

.details-wrapper td {
  padding: 0;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.details-content {
  padding: 0;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  /* padding: 14px 18px; */
  position: absolute;
  top: 11.2%;
  right: 0;
  z-index: 20;
}

.close-detail-window-btn {
  position: absolute;
  right: 0px;
  width: 20px;
  color: red;
  font-size: 20px;
  z-index: 999;
  top: -2px;
  cursor: pointer;
}

/* Subtable */
.qc-subtable {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.qc-subtable th {
  width: 160px;
  text-align: left;
  padding: 6px 5px;
  color: #374151;
  font-weight: 600;
}

.qc-subtable td {
  padding: 6px 5px;
}

.qc-subtable ul {
  padding-left: 18px;
  margin: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.25s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* details row */
.qc-details-row td {
  padding: 8px 10px 10px;
  border-bottom: 1px solid #e5e7eb;
}

.qc-details-row {
  background: #f9fafb;
}

.qc-details {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
}

.qc-detail-group {
  min-width: 180px;
  max-width: 280px;
}

.qc-detail-group h4 {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.qc-detail-group p {
  margin: 0;
  font-size: 12px;
  color: #253c6e;
}

/* reuse chip list for SKUs / POs / Certs */
.qc-details .item-sku-wrapper {
  max-width: 100%;
}

/* numeric alignment */
.qc-num {
  text-align: right;
}

/* sort indicators */
.sort-icon {
  font-size: 9px;
  margin-left: 4px;
  opacity: 0.4;
}

.sorted-asc .sort-icon,
.sorted-desc .sort-icon {
  opacity: 1;
}

.sorted-asc .sort-icon::after,
.sorted-desc .sort-icon::after {
  content: "";
}

/* we can differentiate visually if you want later, but for now
   'sorted-asc' / 'sorted-desc' just bump opacity */

/* expand toggle button */
.expand-toggle {
  margin-left: 4px;
}

.action-btns {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

.qc-table tbody tr td:last-child {
  text-align: right;
}

/* chip list for SKUs / POs / Certs */

.item-sku-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 210px;
}

.item-sku-wrapper ol {
  list-style: none;
  padding: 0;
  margin: 0;
  display: contents;
}

.item-sku-wrapper li {
  display: inline-flex;
  padding: 2px 6px;
  border-radius: 999px;
  background: #f3f4f6;
  font-size: 11px;
  max-width: fit-content;
}

/* numeric alignment (Qty, Failed, etc.) */
.qc-table td,
.qc-table th {
  text-align: center !important;
}

/* ===== Existing styles you already had (kept) ===== */

.toggle_tabs {
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  padding-bottom: 0;
  width: 97%;
  overflow: hidden;
  overflow-x: auto;
  margin: auto;
}

.toggle_tabs .tab {
  cursor: pointer;
  border: 2px solid #eaeaea;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1% 3%;
  border-radius: 0;
  background: transparent;
  border: none;
  font-weight: bold;
  border-bottom: 3px solid #eaeaea;
  text-transform: uppercase;
  color: rgba(27, 105, 111, 0.7);
}

.toggle_tabs .tab.active {
  background: #0024d6 !important;
  border-bottom: 3px solid #1f479a;
  padding: 1% 3%;
  color: white;
}

.toggle_tabs .tab.active p {
  color: white !important;
  font-size: 16px !important;
  font-weight: 700 !important;
}

.toggle_tabs .tab p {
  margin: 0;
  font-size: 16px !important;
  font-weight: 700 !important;
  padding: 1% 3%;
}

.results {
  max-height: 250px;
  width: 180px;
  overflow-y: auto;
  overflow-x: hidden;
  text-align: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
}

.result-list-item {
  text-decoration: underline;
  margin-bottom: 0.2rem;
}


.action-btns {
  display: flex;
}

.content {
  max-width: 100px;
}

.notes {
  width: 150px;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  max-height: 150px;
  padding: 5px;
}

.expanded_notes {
  width: 100%;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  max-height: 227px;
  padding: 5px;
}

.box-container-td {
  /* display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 62px; */
}

.md-button {
  z-index: 0 !important;
}

.header {
  text-align: center;
  border-bottom: rgba(0, 0, 0, 0.49) dashed 0.5px;
}

.right {
  width: 50%;
}

.search-filter {
  position: sticky;
}

.image-container {
  display: flex;
  justify-content: center;
}

.sku-container {
  max-width: 100px;
  overflow-x: auto;
}

.no-image {
  border: 0.5px dashed black;
  height: 500px;
  width: 100%;
  text-align: center;
  font-size: 20px;
  padding-top: 28%;
}

.failed-items {
  font-weight: 900;
}


/* ---- Add to your existing styles ---- */
.qc-header-label {
  font-weight: 900;
}

/* Tabs are now <button>s: strip default button styling */
.toggle_tabs .tab {
  background: none;
  border: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

.toggle_tabs .tab:focus-visible {
  outline: 2px solid #253c6e;
  outline-offset: 2px;
}

.qc-main-row td {
  text-align: center;
  vertical-align: middle;
}

/* Flex lives inside the cell; display:flex on a <td> breaks table layout */
.qc-cell-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.notes {
  margin: 0;
  padding-left: 18px;
  text-align: left;
}

.qc-note-line {
  padding: 5px;
  text-align: left;
}

.qc-line-header td {
  padding: 8px 10px;
  background: #eef1f8;
  color: #253c6e;
  font-weight: 600;
  text-align: left;
  border-top: 1px solid #dfe3ee;
}

.qc-line-count {
  margin-left: 8px;
  font-weight: 400;
  font-size: 12px;
  color: #6b7280;
}

.qc-line-failed {
  color: #b91c1c;
  font-weight: 600;
}

.qc-shipped-date {
  font-weight: 900;
  color: green;
}

.qc-tracking-link {
  color: #253c6e !important;
  text-decoration: underline;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ===== Responsive tweaks ===== */

@media (max-width: 960px) {
  .qc-search-row {
    align-items: flex-start;
  }

  .filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .filters {
    grid-template-columns: 1fr;
  }

  .filter-status {
    grid-column: span 1;
  }

  .qc-search-row {
    flex-direction: column;
  }

  .qc-search-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
